<template>
  <div class="phone-mockup">
    <div
      class="phone-frame"
      :class="{ 'hover-enabled': enableHover }"
      :style="{
        maxWidth: maxWidth,
        maxHeight: maxHeight,
        '--rotate-y': `${rotateY}deg`,
      }"
    >
      <div class="phone-screen">
        <slot />
      </div>
      <div class="phone-button"></div>
      <div class="phone-volume-down"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

const props = withDefaults(
  defineProps<{
    maxWidth?: string;
    maxHeight?: string;
    enableHover?: boolean;
    rotateDirection?: "left" | "right";
  }>(),
  {
    maxWidth: "350px",
    maxHeight: "800px",
    enableHover: false,
    rotateDirection: "left",
  },
);

const rotateY = computed(() => (props.rotateDirection === "left" ? -8 : 8));
</script>

<style scoped>
.phone-mockup {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
}

.phone-frame {
  position: relative;
  width: fit-content;
  height: fit-content;
  background: linear-gradient(
    145deg,
    #3f4652 0%,
    #2d3542 25%,
    #1e2430 60%,
    #151921 100%
  );
  border-radius: 40px;
  padding: 15px;
  box-shadow:
    0 35px 70px rgba(0, 0, 0, 0.5),
    inset -2px -2px 4px rgba(0, 0, 0, 0.7),
    inset 2px 2px 4px rgba(255, 255, 255, 0.15),
    inset 0 0 1px rgba(255, 255, 255, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.08);
  background-clip: padding-box;
}

.phone-frame.hover-enabled {
  transform: perspective(1000px) rotateY(0deg) rotateX(0deg) translateZ(0px);
  transition:
    transform 0.6s ease,
    box-shadow 0.6s ease;
}

.phone-frame.hover-enabled:hover {
  transform: perspective(1000px) rotateY(var(--rotate-y)) rotateX(3deg)
    translateZ(20px);
  box-shadow:
    0 45px 90px rgba(0, 0, 0, 0.6),
    inset -2px -2px 4px rgba(0, 0, 0, 0.7),
    inset 2px 2px 4px rgba(255, 255, 255, 0.15),
    inset 0 0 1px rgba(255, 255, 255, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.08);
}

.phone-frame::before {
  content: "";
  position: absolute;
  top: 10px;
  left: 10px;
  right: 10px;
  height: 60px;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.2) 0%,
    rgba(255, 255, 255, 0.05) 50%,
    transparent 100%
  );
  border-radius: 30px;
  pointer-events: none;
  z-index: 1;
}

.phone-frame::after {
  content: "";
  position: absolute;
  left: -4px;
  top: 120px;
  width: 4px;
  height: 40px;
  background: linear-gradient(90deg, #1e2430 0%, #3f4652 50%, #2d3542 100%);
  border-radius: 4px 0 0 4px;
  box-shadow:
    inset 1px 0 2px rgba(255, 255, 255, 0.3),
    inset -1px 0 1px rgba(0, 0, 0, 0.5);
}

.phone-volume-down {
  position: absolute;
  left: -4px;
  top: 170px;
  width: 4px;
  height: 40px;
  background: linear-gradient(90deg, #1e2430 0%, #3f4652 50%, #2d3542 100%);
  border-radius: 4px 0 0 4px;
  box-shadow:
    inset 1px 0 2px rgba(255, 255, 255, 0.3),
    inset -1px 0 1px rgba(0, 0, 0, 0.5);
}

.phone-screen {
  position: relative;
  width: 100%;
  height: 100%;
  background: #000;
  border-radius: 22px;
  overflow: hidden;
  box-shadow:
    inset 0 0 10px rgba(0, 0, 0, 0.5),
    0 0 0 1px rgba(255, 255, 255, 0.15);
  display: flex;
}

.phone-screen :deep(img) {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.phone-button {
  position: absolute;
  right: -4px;
  top: 140px;
  width: 4px;
  height: 60px;
  background: linear-gradient(270deg, #1e2430 0%, #3f4652 50%, #2d3542 100%);
  border-radius: 0 4px 4px 0;
  box-shadow:
    inset -1px 0 2px rgba(255, 255, 255, 0.3),
    inset 1px 0 1px rgba(0, 0, 0, 0.5);
}
</style>
